<template>
  <div class="flex items-stretch">
    <LayoutHeaderAccountSwitchList
      v-if="isSwitching"
      @select="isSwitching = false"
    />

    <div
      v-if="isDesktop || !isSwitching"
      class="container-scrollbar max-h-[80vh] w-[320px] overflow-y-auto rounded-xl bg-white p-3 shadow-normal shadow-gray-400 tablet:w-[360px] desktop:max-h-[calc(100vh-100px)] desktop:w-[400px]"
    >
      <div class="flex flex-col gap-3 p-3 tablet:gap-5">
        <div
          v-if="profile.hasMultipleAccounts"
          class="mb-1 flex w-full items-center justify-end gap-5"
        >
          <p class="flex-1 truncate text-sm text-gray-700 tablet:text-base">
            {{ profile.accountName }}
          </p>
          <button
            class="text-sm font-bold text-blue-400 underline tablet:text-base"
            @click="isSwitching = true"
          >
            {{ $t('header.account.switch') }}
          </button>
        </div>

        <div class="flex flex-col">
          <div class="flex items-center gap-5">
            <p class="flex-1 truncate text-lg font-bold tablet:text-2xl">
              {{ userName }}
            </p>
            <span
              v-if="userRole"
              class="flex w-20 items-center justify-center rounded p-1.5 text-xs"
              :class="memberBadgeClass"
            >
              {{ userRole }}
            </span>
          </div>
          <p class="truncate text-sm tablet:text-base">{{ profile.email }}</p>
        </div>

        <div class="flex flex-col gap-3 pt-3 tablet:gap-5">
          <TheButton
            :to="editProfilePath"
            :title="$t('header.account.manage-profile')"
            class="w-full text-sm tablet:text-base"
          >
            <span class="font-bold">
              {{ $t('header.account.manage-profile') }}
            </span>
          </TheButton>
          <TheButton
            type="tonal"
            :title="$t('header.account.sign-out')"
            class="w-full text-sm tablet:text-base"
            @click="handleLogout"
          >
            <span class="font-bold">{{ $t('header.account.sign-out') }}</span>
          </TheButton>
        </div>
      </div>

      <ul class="text-sm tablet:text-base">
        <li>
          <TheNuxtLink
            :to="`${consoleDomain}/${locale}/bills`"
            target="_self"
            :title="$t('header.account.bills')"
            data-test="header__usermenu-link"
            class="block rounded-xl p-3 font-bold hover:bg-gray-600_a25 active:bg-gray-600_a35 tablet:p-5"
          >
            {{ $t('header.account.bills') }}
          </TheNuxtLink>
        </li>
        <li>
          <TheNuxtLink
            :to="`${consoleDomain}/${locale}/gift-values`"
            target="_self"
            :title="$t('header.account.gift-values')"
            data-test="header__usermenu-link"
            class="block rounded-xl p-3 hover:bg-gray-600_a25 active:bg-gray-600_a35 tablet:p-5"
          >
            <span class="font-bold">
              {{ $t('header.account.gift-values') }}
            </span>
            <p class="mt-3 text-xs text-gray-700 tablet:text-sm">
              <span class="mr-1">{{ $t('header.account.balance') }}:</span>
              <span class="font-bold">{{ userBalance }}</span>
            </p>
          </TheNuxtLink>
        </li>
        <li>
          <TheNuxtLink
            :to="`${consoleDomain}/${locale}/membership`"
            target="_self"
            :title="$t('header.account.member')"
            data-test="header__usermenu-link"
            class="block rounded-xl p-3 hover:bg-gray-600_a25 active:bg-gray-600_a35 tablet:p-5"
          >
            <span class="font-bold">{{ $t('header.account.member') }}</span>
            <i18n-t
              v-if="amountInfo.levelEndDate"
              keypath="header.account.member-valid-until"
              tag="p"
              class="mt-3 text-xs text-gray-700 tablet:text-sm"
            >
              <template #member>{{ accumulatedMemberType }}</template>
              <template #date>
                <span class="font-bold">{{ levelEndDate }}</span>
              </template>
            </i18n-t>
          </TheNuxtLink>
        </li>
        <li v-if="profile.isAdmin">
          <TheNuxtLink
            :to="`${consoleDomain}/${locale}/team-management`"
            target="_self"
            :title="$t('header.account.team-management')"
            data-test="header__usermenu-link"
            class="block rounded-xl p-3 font-bold hover:bg-gray-600_a25 active:bg-gray-600_a35 tablet:p-5"
          >
            {{ $t('header.account.team-management') }}
          </TheNuxtLink>
        </li>
        <li>
          <TheNuxtLink
            :to="`${consoleDomain}/${locale}/licenses`"
            target="_self"
            :title="$t('header.account.licenses')"
            data-test="header__usermenu-link"
            class="block rounded-xl p-3 font-bold hover:bg-gray-600_a25 active:bg-gray-600_a35 tablet:p-5"
          >
            {{ $t('header.account.licenses') }}
          </TheNuxtLink>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
  const runtimeConfig = useRuntimeConfig();
  const { API_DOMAIN } = runtimeConfig.public;
  const { locale } = useI18n();
  const dayjs = useDayjs();

  const { consoleDomain } = useWebsite();
  const { isDesktop } = useScreen();

  const profileStore = useProfileStore();

  const {
    profile,
    companyAccounts,
    amountInfo,
    userName,
    userRole,
    userMemberType,
    userBalance,
  } = storeToRefs(profileStore);

  const { provideMemberBadgeClass } = provideMemberStyle();

  const isSwitching = ref<boolean>(false);

  const levelEndDate = computed((): string => {
    return dayjs(amountInfo.value.levelEndDate).format('YYYY/MM/DD');
  });

  const accumulatedMemberType = computed((): string => {
    return provideMemberTypeName(
      amountInfo.value.memberType,
      userMemberType.value
    );
  });

  const memberBadgeClass = computed((): string => {
    return provideMemberBadgeClass(userMemberType.value);
  });

  const editProfilePath = computed((): string => {
    const callbackUrl = window.location.href;
    return `${API_DOMAIN}/marketplace/api/v1/profile/edit?websiteCode=${locale.value}&callbackUrl=${callbackUrl}`;
  });

  const handleLogout = (): void => {
    profileStore.HANDLE_LOGOUT();
  };

  onMounted(() => {
    profileStore.GET_AMOUNT_INFO();

    if (profile.value.hasMultipleAccounts && !companyAccounts.value.length) {
      profileStore.GET_COMPANY_ACCOUNTS();
    }
  });
</script>
